import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { BaseFieldProperties, FieldInput } from 'src/api/process-module';
import { TEST_IDS } from 'src/test-ids';
import { getConfigForType } from './getConfigForType';

interface FieldNameLabelInputsProps {
  field: FieldInput;
  index: number;
  handleFieldChange: (index: number, field: Partial<FieldInput>) => void;
  isSubmitting: boolean;
  isFieldUsingExisting: (index: number) => boolean;
  disableNameEdit?: boolean;
}

export type FieldComponentConfig = {
  nameLabel: string;
  labelLabel: string;
  hideName: boolean;
  hideLabel: boolean;
  hideRequired: boolean;
  nameHelperText?: string;
  labelHelperText?: string;
  showProperties: {
    visible: boolean;
    enabled: boolean;
    accessLevel: boolean;
    placeholder: boolean;
  };
};

export const FieldNameLabelInputs = ({
  field,
  index,
  handleFieldChange,
  isSubmitting,
  isFieldUsingExisting,
  disableNameEdit = false,
}: FieldNameLabelInputsProps) => {
  const config = getConfigForType(field.type);
  const { labelLabel, hideLabel, hideRequired, labelHelperText, showProperties: propertyFlags } = config;
  const properties = field.properties;

  return (
    <>
      {/* {!hideName && (
        <>
          <TextField
            fullWidth
            label={nameLabel}
            value={field.name}
            onChange={(e) =>
              handleFieldChange(index, {
                name: e.target.value,
              })
            }
            required
            disabled={isSubmitting || isFieldUsingExisting(index) || disableNameEdit}
          />
          {nameHelperText && <FormHelperText>{nameHelperText}</FormHelperText>}
        </>
      )} */}

      {!hideLabel && (
        <>
          <TextField
            fullWidth
            label={labelLabel}
            value={field.label}
            onChange={(e) =>
              handleFieldChange(index, {
                label: e.target.value,
              })
            }
            required
            disabled={isSubmitting || isFieldUsingExisting(index)}
            inputProps={{ 'data-testid': TEST_IDS.FIELD_EDITOR.LABEL_INPUT }}
          />
          <FormHelperText>{labelHelperText}</FormHelperText>
        </>
      )}

      {propertyFlags.visible && (
        <>
          <Paper sx={{ p: 2 }} elevation={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={properties?.visible ?? true}
                  onChange={(e) =>
                    handleFieldChange(index, {
                      properties: {
                        ...properties,
                        visible: e.target.checked,
                      },
                    } as BaseFieldProperties)
                  }
                  disabled={isSubmitting || isFieldUsingExisting(index)}
                />
              }
              label="Visible"
            />
            <Typography variant="caption" color="text.secondary">
              Whether the field is visible to the user.
            </Typography>
          </Paper>
        </>
      )}
      {propertyFlags.enabled && (
        <>
          <Paper sx={{ p: 2 }} elevation={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!field.properties?.disabled}
                  onChange={(e) => {
                    handleFieldChange(index, {
                      properties: {
                        ...properties,
                        disabled: !e.target.checked,
                      },
                    } as BaseFieldProperties);
                  }}
                />
              }
              label="Enabled"
            />
            <Typography variant="caption" color="text.secondary">
              Whether the field is enabled.
            </Typography>
          </Paper>
        </>
      )}

      {propertyFlags.accessLevel && (
        <FormControl fullWidth>
          <InputLabel>Access Level</InputLabel>
          <Select
            value={properties?.access || 'editor'}
            label="Access Level"
            onChange={(e) =>
              handleFieldChange(index, {
                properties: {
                  ...properties,
                  access: e.target.value as 'editor' | 'manager',
                },
              } as BaseFieldProperties)
            }
            disabled={isSubmitting || isFieldUsingExisting(index)}
            data-testid={TEST_IDS.FIELD_EDITOR.ACCESS_SELECT}
          >
            <MenuItem value="editor">Editor</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
          </Select>
        </FormControl>
      )}

      {propertyFlags.placeholder && (
        <TextField
          fullWidth
          label="Placeholder"
          value={properties?.placeholder || ''}
          onChange={(e) =>
            handleFieldChange(index, {
              properties: {
                ...properties,
                placeholder: e.target.value,
              },
            } as BaseFieldProperties)
          }
          disabled={isSubmitting || isFieldUsingExisting(index)}
        />
      )}

      {!hideRequired && (
        <FormControlLabel
          control={
            <Switch
              checked={field.required}
              onChange={(e) =>
                handleFieldChange(index, {
                  required: e.target.checked,
                })
              }
              disabled={isSubmitting || isFieldUsingExisting(index)}
            />
          }
          label="Required"
        />
      )}
    </>
  );
};
